import BeeswaxPacing from '@/Enums/Beeswax/BeeswaxPacing.enum';
import LineItemFormTab from '@/Enums/CampaignManagement/LineItemFormTab.enum';
import { ILineItemForm } from '@/Interfaces/CampaignManagement/LineItem.interface';
import BlurBackdrop from '@/components/Backdrop/BlurBackdrop';
import DuplicateButton from '@/components/Buttons/DuplicateButton';
import CheckIcon from '@/components/Icons/checkIcon';
import ConfirmationModal from '@/components/Modal/ConfirmationModal';
import { NEW_ITEM_ID } from '@/constants/campaignManagement';
import { lineItemApiDataToFormStructure } from '@/helpers/campaignManagement/lineItemForm';
import {
  DEFAULT_ACTIVE_TAB,
  LINE_ITEM_TABS,
} from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/constants/lineItemTabs';
import UserTypeCheckbox from '@/pages/UserManagement/Components/UserTypeCheckbox';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectLineItem } from '@/store/selectors/campaignManagement.selectors';
import { selectWhitelabelBillingModel } from '@/store/selectors/currentWhitelabel.selectors';
import { selectSharedApiDealGroupControlOptions, selectSharedApiPackages } from '@/store/selectors/sharedApi.selectors';
import {
  fetchLineItemDomainLists,
  fetchLineItemHasUnmanagedBeeswaxBiddingSettings,
} from '@/store/slices/campaignManagementSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Button } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import moment from 'moment';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import ForecasterWidget from './ForecasterWidget';
import LineItemDuplicationModal from './LineItemDuplicationModal';
import LineItemStatus from './components/LineItemStatus';
import LineItemStatusChangeButton from './components/LineItemStatusChangeButton';
import SavingDots from './components/SavingDots';
import { EXPAND_ICON_CLASSNAME, LINE_ITEM_TABS_COMPONENTS, defaultFormData } from './constants/';
import { getDirtyTabs } from './helpers';
import { lineItemFormValidationSchema } from './validationSchema';

interface ISaveData {
  formData: ILineItemForm;
  changedValues: Partial<ILineItemForm>;
}

interface IProps {
  id: number | string;
  index: number;
  onSave: (data: ISaveData) => Promise<boolean>;
  defaultExpanded?: boolean;
  readOnly?: boolean;
}

const LineItemForm = memo(({ id, index, onSave, defaultExpanded = false, readOnly }: IProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const billingModel = useAppSelector(selectWhitelabelBillingModel);

  const resolver = useMemo(() => yupResolver(lineItemFormValidationSchema(billingModel)), [billingModel]);
  const methods = useForm({
    //@ts-expect-error todo types
    resolver,
    defaultValues: defaultFormData,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    getValues,
    reset,
    resetField,
    control,
    formState: { isValid, isDirty, dirtyFields },
  } = methods;

  const getFormData = useCallback((): ISaveData => {
    const formData = getValues();
    const changedValues = Object.keys(dirtyFields).reduce(
      (acc, key) => {
        if (key === 'audiences') {
          acc.audiences = formData.audiences;
          acc.channel = formData.channel;
          return acc;
        }

        acc[key] = formData[key];
        return acc;
      },
      {} as Partial<ILineItemForm>,
    );

    return {
      formData,
      changedValues,
    };
  }, [getValues, dirtyFields]);

  const watchName = useWatch({ control, name: 'name' });
  const displayName = watchName || `Line item ${index + 1}`;

  const [activeTab, setActiveTab] = useState(DEFAULT_ACTIVE_TAB);
  const [savedTabs, setSavedTabs] = useState<LineItemFormTab[]>([]);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isLoading, setIsLoading] = useState(false);
  const [isLineItemDuplicationModalOpen, setIsLineItemDuplicationModalOpen] = useState(false);
  const [isFormReady, setIsFormReady] = useState(id === NEW_ITEM_ID);

  const activeLineItem = useAppSelector(state => selectLineItem(state, index));
  const { dealGroups } = useAppSelector(selectSharedApiDealGroupControlOptions);
  const { packages } = useAppSelector(selectSharedApiPackages);
  const hasPackages = useMemo<boolean>(() => !!packages?.length, [packages?.length]);
  const showDealGroups = useMemo<boolean>(() => !!dealGroups?.length || hasPackages, [dealGroups?.length, hasPackages]);
  const getTabLabel = useCallback(
    (tab: LineItemFormTab) => (tab === LineItemFormTab.DealGroups && hasPackages ? 'Packages' : tab),
    [hasPackages],
  );
  const isActiveTabSummary = useMemo(() => activeTab === LineItemFormTab.Summary, [activeTab]);
  /** Hide Audience tab for Beeswax Line Items */
  const showAudience = useMemo<boolean>(
    () => id === NEW_ITEM_ID || activeLineItem?.isRibeyeEntry,
    [id, activeLineItem?.isRibeyeEntry],
  );

  const watchLineItemsEndDate = useWatch({ control, name: 'endDate' });
  const watchLineItemsCreatives = useWatch({ control, name: 'creatives' });

  const watchPacing = useWatch({ control, name: 'pacing' });
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalConfirmed, setModalConfirmed] = useState<boolean>(false);

  // Not on activeLineItem (the bulk campaign fetch) — the backend only computes this on a
  // single-item GET to avoid one Beeswax call per line item on every campaign load, so it's
  // fetched separately here when the form opens (flank#4301). isCheckingBiddingSettings starts
  // true for an existing line item (false has no meaning until the check actually runs) so a
  // save can't slip through unwarned while it's in flight; a failed check fails safe to "warn"
  // rather than silently "safe to save".
  const [hasUnmanagedBiddingSettings, setHasUnmanagedBiddingSettings] = useState<boolean>(false);
  const [isCheckingBiddingSettings, setIsCheckingBiddingSettings] = useState<boolean>(id !== NEW_ITEM_ID);
  const [showUnmanagedBiddingModal, setShowUnmanagedBiddingModal] = useState<boolean>(false);
  const [unmanagedBiddingModalConfirmed, setUnmanagedBiddingModalConfirmed] = useState<boolean>(false);

  const showDisclaimer = useMemo(() => {
    let isLineItemDateLater = false;
    const lineItemEndDate = watchLineItemsEndDate?.clone();
    watchLineItemsCreatives.forEach(creative => {
      const creativeEndDate = moment.utc(creative.endDate);
      if (lineItemEndDate?.isValid() && creativeEndDate?.isValid()) {
        if (creativeEndDate.isAfter(lineItemEndDate)) {
          isLineItemDateLater = true;
        }
      }
    });
    return isLineItemDateLater;
  }, [watchLineItemsEndDate, watchLineItemsCreatives]);

  useEffect(() => {
    if (activeLineItem) {
      const formData = lineItemApiDataToFormStructure(activeLineItem);
      reset(formData);
      setSavedTabs(getDirtyTabs(formData));
      setIsFormReady(true);

      if (activeLineItem.has_custom_domain_lists) {
        dispatch(fetchLineItemDomainLists(activeLineItem.id))
          .unwrap()
          .then(({ allowDomains, blockDomains }) => {
            methods.setValue('customAllowDomains', allowDomains ?? []);
            methods.setValue('customBlockDomains', blockDomains ?? []);
          })
          .catch(() => {
            /* intentionally swallowed — non-critical fetch */
          });
      }

      if (id !== NEW_ITEM_ID) {
        setIsCheckingBiddingSettings(true);
        dispatch(fetchLineItemHasUnmanagedBeeswaxBiddingSettings(activeLineItem.id))
          .unwrap()
          .then(setHasUnmanagedBiddingSettings)
          .catch(() => {
            // Couldn't confirm either way — fail safe to "warn" rather than "safe to save".
            setHasUnmanagedBiddingSettings(true);
          })
          .finally(() => setIsCheckingBiddingSettings(false));
      }
    }
  }, [activeLineItem, reset, dispatch, methods, id]);

  useEffect(() => {
    if (defaultExpanded && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [defaultExpanded]);

  const handleTabSelect = useCallback((name: LineItemFormTab) => {
    setActiveTab(name);
    setIsExpanded(true);
  }, []);

  const toggleTabExpansion = useCallback(() => setIsExpanded(prevExpanded => !prevExpanded), []);

  const handleSave = useCallback(async () => {
    if (!isActiveTabSummary && !isLineItemDuplicationModalOpen) {
      setActiveTab(LineItemFormTab.Summary);
      return;
    }

    if (watchPacing === BeeswaxPacing.ASAP && !modalConfirmed && !isLineItemDuplicationModalOpen) {
      setShowModal(true);
      return;
    }

    // Block rather than silently allow the save — the check hasn't reported back yet, so
    // there's no "safe to save" answer to act on either way.
    if (isCheckingBiddingSettings) {
      return;
    }

    if (hasUnmanagedBiddingSettings && !unmanagedBiddingModalConfirmed && !isLineItemDuplicationModalOpen) {
      setShowUnmanagedBiddingModal(true);
      return;
    }
    setIsLoading(true);
    try {
      const ok = await onSave(getFormData());
      if (ok) {
        if (isLineItemDuplicationModalOpen) {
          setIsLineItemDuplicationModalOpen(false);
        }
        const dirtyTabs = getDirtyTabs(getValues());
        setSavedTabs(dirtyTabs);
      }
    } finally {
      setIsLoading(false);
      setModalConfirmed(false);
      setUnmanagedBiddingModalConfirmed(false);
    }
  }, [
    onSave,
    getFormData,
    isLineItemDuplicationModalOpen,
    isActiveTabSummary,
    showModal,
    modalConfirmed,
    hasUnmanagedBiddingSettings,
    isCheckingBiddingSettings,
    unmanagedBiddingModalConfirmed,
  ]);

  useEffect(() => {
    if (modalConfirmed || unmanagedBiddingModalConfirmed) {
      handleSave();
    }
  }, [modalConfirmed, unmanagedBiddingModalConfirmed]);

  const handleLineItemDuplicationModalClose = useCallback(() => {
    resetField('duplicates');
    setIsLineItemDuplicationModalOpen(false);
  }, [resetField]);

  const onClickDuplicate = useCallback(() => setIsLineItemDuplicationModalOpen(true), []);

  const tabs = useMemo(() => {
    const baselineTabs = showAudience ? LINE_ITEM_TABS : LINE_ITEM_TABS.filter(tab => tab !== LineItemFormTab.Audience);

    if (showDealGroups) {
      return baselineTabs;
    }
    return baselineTabs.filter(tab => tab !== LineItemFormTab.DealGroups);
  }, [showAudience, showDealGroups]);

  const ActiveTab = useMemo(() => LINE_ITEM_TABS_COMPONENTS[activeTab], [activeTab]);

  if (activeLineItem?.is_archived) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <LineItemDuplicationModal
        isOpen={isLineItemDuplicationModalOpen}
        handleClose={handleLineItemDuplicationModalClose}
        handleSave={handleSave}
        isLoading={isLoading}
      />
      <Paper className='col-span-3 mt-8 !bg-[#E4E8E6]' ref={ref}>
        <div className='flex justify-between items-center gap-4 px-7 py-4'>
          <div className='flex flex-col gap-3 min-w-0'>
            <div
              className='flex items-center justify-between gap-3 w-full text-left text-xl rounded-t-md font-semibold'
              onClick={toggleTabExpansion}
            >
              <div className='flex items-center gap-3 min-w-0'>
                <span className='font-semibold text-[#1E1E1E] text-xl cursor-pointer truncate' title={displayName}>
                  {displayName}
                </span>
                <div className='flex items-center gap-3 shrink-0' onClick={event => event.stopPropagation()}>
                  <DuplicateButton onClick={onClickDuplicate} disabled={!isValid || !activeLineItem?.isRibeyeEntry} />
                  <LineItemStatusChangeButton
                    activeLineItem={activeLineItem}
                    loading={isLoading}
                    setLoading={setIsLoading}
                  />
                  <LineItemStatus status={activeLineItem?.status} />
                </div>
              </div>
              {isExpanded ? (
                <ExpandLess className={EXPAND_ICON_CLASSNAME} />
              ) : (
                <ExpandMore className={EXPAND_ICON_CLASSNAME} />
              )}
            </div>
            <div className='flex gap-2'>
              {tabs.map(name => (
                <UserTypeCheckbox
                  checked={name === activeTab}
                  key={name}
                  label={getTabLabel(name)}
                  name={name}
                  isCreateCampaign={true}
                  onClick={() => {
                    handleTabSelect(name);
                  }}
                  labelIcon={
                    savedTabs.includes(name) && (
                      <CheckIcon className={'ml-1 flex item-center'} color={name === activeTab ? '#FFF' : '#737373'} />
                    )
                  }
                />
              ))}
            </div>
            {showDisclaimer && (
              <span className='text-[red] text-[0.75rem] '>
                Creative flighting for this line item does not align with line item end date.
              </span>
            )}
          </div>
          {isFormReady && <ForecasterWidget lineItemIndex={index} isExpanded={isExpanded} />}
        </div>

        <Collapse in={isExpanded} timeout={500}>
          <BlurBackdrop open={isLoading} backdropContent={<SavingDots />}>
            <form
              className='flex min-h-[740px] bg-[#FFF] py-4 px-7 flex-col justify-between relative h-full'
              onSubmit={handleSubmit(handleSave)}
            >
              <ActiveTab index={index} />
              <div className='mt-4 flex items-center gap-2 justify-end'>
                {isActiveTabSummary ? (
                  <span>
                    By clicking <i>Confirm</i>, you agree that the above parameters are accurate and approved for
                    execution.
                  </span>
                ) : null}
                <Button
                  type='submit'
                  variant='contained'
                  className='bg-[#1B4332]'
                  sx={{
                    height: '48px',
                    width: '130px',
                  }}
                  // review, Lucas: handleSave already no-ops while the Beeswax bidding-settings
                  // check is in flight (flank#4301), but the button looked clickable the whole
                  // time — disabling it and naming the state is the only user-visible sign
                  // anything is happening.
                  disabled={!isValid || !isDirty || readOnly || isCheckingBiddingSettings}
                >
                  {isCheckingBiddingSettings ? 'Checking…' : isActiveTabSummary ? 'Confirm' : 'Save'}
                </Button>
              </div>
            </form>
          </BlurBackdrop>
        </Collapse>
        <ConfirmationModal
          open={showModal}
          onClose={() => setShowModal(false)}
          onConfirm={() => {
            setModalConfirmed(true);
            setShowModal(false);
          }}
          text='Are you sure you want to have ASAP Pacing? ASAP Pacing could lead to delivering all impressions in the next few hours.'
        />
        <ConfirmationModal
          open={showUnmanagedBiddingModal}
          onClose={() => setShowUnmanagedBiddingModal(false)}
          onConfirm={() => {
            setUnmanagedBiddingModalConfirmed(true);
            setShowUnmanagedBiddingModal(false);
          }}
          text="There are bidding settings for this line item that Flank doesn't track. Saving will overwrite them."
        />
      </Paper>
    </FormProvider>
  );
});

export default LineItemForm;
